import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { jwtDecode } from 'jwt-decode'

interface JwtPayload {
  sub: number
  tenant_id: number
  exp: number
  iat: number
}

interface User {
  pk: number
  public_id: string
  email: string
  first_name: string
  last_name: string
  preferred_language: string
  is_superadmin: boolean
  roles?: string[]
  permissions?: string[]
}

interface TenantInfo {
  public_id: string
  name: string
  slug: string
  features?: string[]
  timezone?: string
  logo_url?: string | null
}

export const useAuthStore = defineStore(
  'auth',
  () => {
    const accessToken = ref<string | null>(null)
    // sessionToken is the RefreshToken UUID, used as X-Session-Token header in
    // allauth headless requests and as the body field for token refresh.
    const sessionToken = ref<string | null>(null)
    const user = ref<User | null>(null)
    const activeTenant = ref<TenantInfo | null>(null)
    const tenants = ref<TenantInfo[]>([])
    const roles = ref<string[]>([])
    const permissions = ref<string[]>([])

    const isAuthenticated = computed(() => !!accessToken.value && !isTokenExpired.value)

    const isTokenExpired = computed(() => {
      if (!accessToken.value) return true
      try {
        const { exp } = jwtDecode<JwtPayload>(accessToken.value)
        return Date.now() / 1000 > exp
      } catch {
        return true
      }
    })

    const tenantId = computed(() => {
      if (!accessToken.value) return null
      try {
        return jwtDecode<JwtPayload>(accessToken.value).tenant_id
      } catch {
        return null
      }
    })

    function setTokens(access: string, session: string) {
      accessToken.value = access
      sessionToken.value = session
    }

    function setAccessToken(access: string) {
      accessToken.value = access
    }

    function setSessionToken(session: string) {
      sessionToken.value = session
    }

    function setUser(u: User) {
      user.value = u
      if (u.roles) {
        roles.value = u.roles
      }
      if (u.permissions) {
        permissions.value = u.permissions
      }
    }

    function setActiveTenant(t: TenantInfo) {
      activeTenant.value = t
    }

    function setTenants(list: TenantInfo[]) {
      tenants.value = list
    }

    function setRoles(r: string[]) {
      roles.value = r
    }

    function setPermissions(p: string[]) {
      permissions.value = p
    }

    function logout() {
      accessToken.value = null
      sessionToken.value = null
      user.value = null
      activeTenant.value = null
      roles.value = []
      permissions.value = []
    }

    return {
      accessToken,
      sessionToken,
      user,
      activeTenant,
      tenants,
      roles,
      permissions,
      isAuthenticated,
      isTokenExpired,
      tenantId,
      setTokens,
      setAccessToken,
      setSessionToken,
      setUser,
      setActiveTenant,
      setTenants,
      setRoles,
      setPermissions,
      logout,
    }
  },
  {
    persist: {
      pick: ['accessToken', 'sessionToken', 'user', 'activeTenant', 'tenants', 'roles', 'permissions'],
    },
  },
)
